<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-12">
        <span
          v-show="loading"
          class="spinner-border spinner-border-sm m-2"
          role="status"
          aria-hidden="true"
        />
      </div>
    </div>
    <div class="form-row">
      <div v-if="supplies.length" class="col-3">
        <supply-list
          :supply="supply"
          :supplies="supplies"
          :transactions-summary="transactionsSummary"
          @select-supply="selectSupply"
          @remove-supply="removeSupply"
        />

        <hr>
        <div class="form-row">
          <label class="col-form-label col-form-label-sm col-2"><strong>Name:</strong></label>
          <div class="form-group col-4">
            <v-text-field
              v-model="newSupply.name"
              type="text"
              density="compact"
              variant="outlined"
              :error-messages="v$.newSupply.name.$errors.map((e) => e.$message)"
              @blur="v$.newSupply.name.$touch()"
            />
          </div>
          <div class="form-group col-4">
            <button
              type="button"
              class="btn btn-default btn-sm"
              @click="addSupply"
            >
              Add New Supply +
              <span
                v-show="addSupplyInProgress"
                class="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </div>

      <div v-if="supply" class="col-9">
        <h5>{{ supply.name }} Records</h5>

        <div class="form-row">
          <label class="col-form-label col-form-label-sm"><strong>Note:</strong></label>
          <div class="form-group col-6">
            <v-text-field
              v-model="transaction.note"
              type="text"
              density="compact"
              autocomplete="off"
              variant="outlined"
              hide-details
            />
          </div>
          <label class="col-form-label col-form-label-sm"><strong>Quantity:</strong></label>
          <div class="form-group col-1">
            <v-text-field
              v-model="transaction.quantity"
              type="number"
              density="compact"
              variant="outlined"
              :error-messages="v$.transaction.quantity.$errors.map((e) => e.$message)"
              @blur="v$.transaction.quantity.$touch()"
            />
          </div>
          <div class="form-group col-2">
            <button
              type="button"
              class="btn btn-default btn-sm"
              @click="addTransaction"
            >
              Add New Record +
              <span
                v-show="addInProgress"
                class="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>

        <table class="table table-striped table-hover table-sm">
          <thead>
            <tr class="d-flex">
              <th class="col-3">
                <strong>Type</strong>
              </th>
              <th class="col-3">
                <strong>Note</strong>
              </th>
              <th class="col-1">
                <strong>Received</strong>
              </th>
              <th class="col-1">
                <strong>Sent</strong>
              </th>
              <th class="col-2">
                <strong>Record Date</strong>
              </th>
              <th class="col-2">
                <strong><a
                  class="decorated"
                  :href="
                    '/supply/transactionsExportXLS.php?supplyID=' + supply.id
                  "
                >Export Records To XLS</a></strong>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(supply_transaction) in supply_transactions"
              :key="supply_transaction.id"
              class="d-flex"
            >
              <td class="col-3">
                <span v-if="supply_transaction.user_supply_transaction != null">
                  <strong>Instructor</strong>
                  <a
                    v-if="supply_transaction.user_supply_transaction.user"
                    class="decorated"
                    :href="
                      '/office/userControl.php?userID=' +
                        supply_transaction.user_supply_transaction.user.id
                    "
                  >
                    {{
                      supply_transaction.user_supply_transaction.user.first_name
                    }}
                    {{
                      supply_transaction.user_supply_transaction.user.last_name
                    }}
                  </a>
                  Order#
                  <a
                    class="decorated"
                    :href="
                      '/supply/supplyOrderView.php?supplyOrderID=' +
                        supply_transaction.user_supply_transaction.id
                    "
                  >
                    {{ supply_transaction.user_supply_transaction.id }}
                  </a>
                </span>
                <span v-if="supply_transaction.user_supply_transaction == null"><strong>Inventory Adjustment</strong></span>
              </td>
              <td class="col-3">
                {{ supply_transaction.note }}
              </td>
              <td class="col-1">
                <font v-show="supply_transaction.quantity >= 0" color="green">
                  +{{ supply_transaction.quantity }}
                </font>
              </td>
              <td class="col-1">
                <font v-show="supply_transaction.quantity < 0" color="red">
                  {{ supply_transaction.quantity }}
                </font>
              </td>
              <td class="col-2">
                {{
                  formatDate(supply_transaction.create_date)
                }}
              </td>
              <td class="col-2">
                <a
                  v-if="supply_transaction.user_supply_transaction == null"
                  href="#"
                  class="decorated"
                  @click.prevent="removeTransaction(supply_transaction)"
                >Delete</a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import axios from 'axios';
import moment from 'moment';

import { appValidator } from '../../common/core/utils/validator';

import SupplyList from './supplyList.vue';

export default {
  components: { SupplyList },
  props: ['value'],
  setup() {
    const formatDate = date => moment(date).format('MM/DD/YYYY');
    return { v$: useVuelidate(), formatDate };
  },
  data() {
    return {
      supplies: [],
      transactionsSummary: [],
      supply: null,
      supply_transactions: [],
      loading: true,
      transaction: {
        note: '',
        quantity: '',
      },
      newSupply: {
        name: '',
      },
      addInProgress: false,
      addSupplyInProgress: false,
    };
  },

  async mounted() {
    await this.loadSupplies();
    // eslint-disable-next-line eqeqeq
    const supply = this.supplies.find(x => x.id == '10011');
    this.selectSupply(supply);
  },

  validations() {
    return {
      transaction: {
        quantity: {
          required: appValidator.required,
        },
      },
      newSupply: {
        name: {
          required: appValidator.required,
        },
      },
    };
  },

  methods: {
    async loadSupplies() {
      this.loading = true;
      const response = await axios.get('/api/supplies');
      this.supplies = response.data.data;
      this.transactionsSummary = response.data.meta.transactions_summary;
      this.loading = false;
    },

    async selectSupply(supply) {
      this.supply = supply;
      this.loadTransactions();
    },

    async loadTransactions() {
      this.loading = true;
      const response = await axios.get(
        `/api/supplies/${this.supply.id}/transactions`,
      );
      this.supply_transactions = response.data;
      this.loading = false;
    },

    async addTransaction() {
      if (this.v$.transaction.$invalid) {
        this.v$.transaction.$touch();
        return;
      }

      this.addInProgress = true;

      const response = await axios.post(
        `/api/supplies/${this.supply.id}/transactions`,
        this.transaction,
      );
      this.addInProgress = false;
      this.transaction.quantity = '';
      this.transaction.note = '';
      this.v$.$reset();
      await this.loadTransactions();
      alert('Transaction has been added successfully!');
    },

    async removeTransaction(transaction) {
      if (confirm('Do you really want to delete transaction?')) {
        const response = await axios.delete(
          `/api/supplies/${this.supply.id}/transactions/${transaction.id}`,
        );
        await this.loadTransactions();
        alert('Transaction has been deleted successfully!');
      }
    },

    async addSupply() {
      if (this.v$.newSupply.$invalid) {
        this.v$.newSupply.$touch();
        return;
      }

      this.addSupplyInProgress = true;

      const response = await axios.post('/api/supplies', this.newSupply);
      this.addSupplyInProgress = false;
      this.newSupply.name = '';
      this.v$.$reset();
      await this.loadSupplies();
      alert('Supply has been added successfully!');
    },

    async removeSupply(supply) {
      if (confirm(`Do you really want to delete supply ${supply.name}?`)) {
        const response = await axios.delete(`/api/supplies/${supply.id}`);
        await this.loadSupplies();
        alert('Supply has been deleted successfully!');
      }
    },
  },
};
</script>
